/**
 * AI:
 * Status: 'in progress'
 * Type: Service
 * SubType: QlBatch
 * Reason: A-2 - the Verlauf of one signing process. SIX reads in ONE `qlService.builder()` chain,
 *         each pipe folded onto `SmTimelineEntryDto[]`, concatenated and sorted ONCE.
 */
import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {FilterCriteria} from 'features/core/shared/filter/api/filter.criteria';
import {Sorting} from 'features/core/shared/misc/sorting';
import {QlQueryDto} from 'features/core/shared/ql/data/dto/ql-query.dto';
import {QlQueryResult} from 'features/core/shared/ql/builder/ql-query-builder';
import {QlService} from 'features/core/shared/ql/service/ql.service';
import {
    MvsFormValueListEntryDto
} from 'features/core/shared/dto/form/dto/mvs-form-value-list-entry.dto';
import {
    SmSigningProcessTimelineDto
} from '../../../model/dto/api/sm-signing-process-timeline.dto';
import {SmTimelineEntryDto} from '../../../model/dto/api/sm-timeline-entry.dto';
import {SmTimelineLaneDto} from '../../../model/dto/api/sm-timeline-lane.dto';
import {SmTimelineSourceEnum} from '../../../model/enum/sm-timeline-source.enum';

/** Pipe names. Also the query names - `QlQueryBuilder` links the two by name. */
export const SM_TL_Q_SIGNERS = 'signers';
export const SM_TL_Q_NOTIFICATIONS = 'signerNotifications';
export const SM_TL_Q_AUDIT_EVENTS = 'auditEvents';
export const SM_TL_Q_SIGNATURE_FIELDS = 'signatureFields';
export const SM_TL_Q_DOCUMENT_VERSIONS = 'documentVersions';
export const SM_TL_Q_SIGNING_LINKS = 'signingLinks';

/**
 * The `cc.ObjectType` alias of a signer. Both polymorphic reads filter on THIS, never on a numeric
 * object-type id - the id is a per-tenant-database fact (1271 on dev/tenantA) and a query pinned to
 * it returns nothing on every other environment.
 */
export const SM_TL_SIGNER_OBJECT_TYPE_ALIAS = 'sm.SmSigningProcessSigner';

/**
 * Heading of the surface. Note what is NOT in it: the completeness word the requirement originally
 * carried and the developer struck. The Verlauf cannot show caseworker
 * notifications as notification rows - they are addressed to a `um.User` and appear here as audit
 * events instead - so a completeness claim would be false. Binding constraint of the signed design.
 */
export const SM_TIMELINE_HEADING = 'Verlauf';

/** Loading state of the Verlauf. */
export const SM_TIMELINE_LOADING = 'Verlauf wird geladen …';

/** Empty state of the Verlauf. */
export const SM_TIMELINE_EMPTY = 'Für diesen Signaturprozess wurden noch keine Vorgänge erfasst.';

/** Error state of the Verlauf. */
export const SM_TIMELINE_ERROR = 'Der Verlauf konnte nicht geladen werden.';

/**
 * Builds the „Verlauf“ of ONE signing process out of six sources, in one HTTP round trip.
 *
 * <h2>Six reads, one request, one sort</h2>
 * The shape is taken from {@code TicketHistoryComponent}: ONE request, a discriminator per row
 * ({@link SmTimelineSourceEnum}), ONE sort over the merged list. It is explicitly NOT the shape of
 * {@code tm/component/protected-components/history/history.component.ts}, which concatenates its
 * sources one after another and never sorts the result - that component is marked
 * {@code NOT_IN_USE_CURRENTLY} and its ordering is wrong by construction, because a per-source
 * concatenation puts every notification after every audit event regardless of when either happened.
 *
 * Parallel RxJS combination operators are forbidden with QL. The batch IS the parallelism: the six
 * queries travel in one {@code QlRequestDto} and the three that depend on the signer id list say so
 * with {@code dependsOn}, which the backend resolves server-side before compiling them.
 *
 * <h2>Where the colours come from - and where they must never come from</h2>
 * Every enum column of every pipe arrives with its value list, because all six pipes are declared
 * with {@code includeForm: true}. The backend builds that list from {@code @UiEnumValueInfo}, so
 * {@code MvsFormValueListEntryDto} carries the {@code label}, the {@code image} (a Font Awesome
 * class), the {@code color} and the {@code backgroundColor} that the backend itself declares -
 * {@code SmAuditEventTypeEnum#dispatchRefused} for instance as {@code fa-solid fa-ban} /
 * {@code red-200} / {@code red-800}. The mappers below put that entry straight onto
 * {@link SmTimelineEntryDto.valueListEntry}.
 *
 * <b>There is deliberately no TypeScript table in this file mapping an event type to a label, an
 * icon or a colour, and none may be added.</b> A second table drifts from the backend the first time
 * anyone appends an enum member, and it drifts silently - the row still renders, just in the wrong
 * colour, on the one surface a caseworker reads to reconstruct what happened. The only fallback here
 * is {@link SmSigningProcessTimelineService.eventTitle}, which names the ordinal it could not
 * resolve rather than inventing a word for it.
 *
 * <h2>Scale</h2>
 * No volume is claimed (FD-5 of the signed design). This reads the whole history of one process
 * unpaged, which is right for the ~50-row order of magnitude a signing process produces and makes no
 * statement about a process with hundreds of events.
 */
@Injectable({
    providedIn: 'root'
})
export class SmSigningProcessTimelineService {

    constructor(protected qlService: QlService) {
    }

    /**
     * Loads the whole Verlauf of one process.
     *
     * Errors are NOT swallowed: the observable fails, so the caller can render its error state. The
     * builder additionally surfaces pipeline errors through {@code MvsMessageService} by default,
     * which is left on.
     *
     * @param signingProcessId the `sm.SmSigningProcess` whose history is wanted
     */
    load(signingProcessId: number): Observable<SmSigningProcessTimelineDto> {

        // ─────────────────────────────────────────────────────────────────────────────────────────
        // ALL SIX PIPES ARE DECLARED EXPLICITLY, and that is not decoration.
        //
        // Every pipe needs `includeForm: true` so its enum columns arrive with the value list the
        // backend builds from @UiEnumValueInfo - that list IS the presentation of the row (see the
        // class comment). But `QlQueryBuilder.buildRequest()` stops auto-generating pipes for EVERY
        // query the moment ONE custom pipe exists (ql-query-builder.ts:225-231). Declaring five and
        // relying on the auto-pipe for the sixth would leave that sixth with no pipe at all, its
        // `pipeData` empty and its rows silently missing from the Verlauf - no error, just a shorter
        // history. Add a query here and you add its pipe in the same edit.
        //
        // `pipe()` hardcodes `type: 'simple'`, which is what gives the alias-prefixed result keys
        // (`a.eventType`, not `eventType`) that every mapper below reads. Never call `.pipeType()`
        // on this chain: it affects only the auto-pipe path, so it would change nothing here while
        // suggesting it had.
        // ─────────────────────────────────────────────────────────────────────────────────────────
        return this.qlService.builder()
            .pipe({name: SM_TL_Q_SIGNERS, simpleQuery: SM_TL_Q_SIGNERS, settings: {includeForm: true}})
            .pipe({name: SM_TL_Q_NOTIFICATIONS, simpleQuery: SM_TL_Q_NOTIFICATIONS, settings: {includeForm: true}})
            .pipe({name: SM_TL_Q_AUDIT_EVENTS, simpleQuery: SM_TL_Q_AUDIT_EVENTS, settings: {includeForm: true}})
            .pipe({
                name: SM_TL_Q_SIGNATURE_FIELDS, simpleQuery: SM_TL_Q_SIGNATURE_FIELDS,
                settings: {includeForm: true}
            })
            .pipe({
                name: SM_TL_Q_DOCUMENT_VERSIONS, simpleQuery: SM_TL_Q_DOCUMENT_VERSIONS,
                settings: {includeForm: true}
            })
            .pipe({name: SM_TL_Q_SIGNING_LINKS, simpleQuery: SM_TL_Q_SIGNING_LINKS, settings: {includeForm: true}})
            // Declaration ORDER matters: a dependency must be declared before the query that
            // declares it in `dependsOn`, so `signers` goes first.
            .query(SmSigningProcessTimelineService.signersQuery(signingProcessId))
            .query(SmSigningProcessTimelineService.signerNotificationsQuery())
            .query(SmSigningProcessTimelineService.auditEventsQuery(signingProcessId))
            .query(SmSigningProcessTimelineService.signatureFieldsQuery())
            .query(SmSigningProcessTimelineService.documentVersionsQuery(signingProcessId))
            .query(SmSigningProcessTimelineService.signingLinksQuery())
            .execute()
            .pipe(map(result => SmSigningProcessTimelineService.mergeTimeline(result)));
    }

    // =================================================================================================
    // The six queries
    // =================================================================================================

    /**
     * Q1 - the signers of the process.
     *
     * Two jobs, and only two: it produces the LANES of the Verlauf, and it is the dependency the
     * three signer-scoped queries (Q2, Q4, Q6) resolve their id list from via
     * {@code ${query:signers:s.id}}.
     *
     * <b>It contributes no timeline ROWS, on purpose.</b> Every moment this entity records -
     * `linkGeneratedAt`, `linkDeliveredAt`, `lastAccessAt`, `linkRevokedAt`, `consentGivenAt`,
     * `otpVerifiedAt`, `identifiedAt`, `completedAt` - already has its own audit event
     * (`linkGenerated`, `linkDelivered`, `linkOpened`, `linkRevoked`, `consentGiven`, `otpVerified`,
     * `signerIdentifiedInPerson`, `signatureSubmitted`), and `signerAdded` is written on all three
     * creation paths (`SmSigningProcessService:1005`, `SmProfileSigningService:1063,2046`). Emitting
     * them here as well would double every one of those rows in the merged list, which reads as if
     * the process had done everything twice.
     *
     * `otpHash` is not projected and could not be served anyway - it is `@UiFieldInfo(uiHidden =
     * true)`, which strips it on the QL path as well as the CRUD path.
     */
    private static signersQuery(signingProcessId: number): QlQueryDto {
        return {
            name: SM_TL_Q_SIGNERS,
            type: 'view',
            start: {
                type: 'entity',
                name: 'sm.SmSigningProcessSigner',
                as: 's',
                fields: [{name: 'id'}, {name: 'signerName'}, {name: 'signerRole'},
                    {name: 'signingOrder'}, {name: 'signerStatus'}]
            },
            fixedFilters: [
                FilterCriteria.create('s.signingProcessId', FilterCriteria.cOperatorEqual, signingProcessId)
            ],
            sortings: [new Sorting('s.signingOrder', true)]
        };
    }

    /**
     * Q2 - the notifications addressed to those signers.
     *
     * Same shape and the same three measured join traps as
     * {@code smSigningProcessNotificationsQuery} (see `sm-signing-process-notifications-ql.ts` and
     * `ns-dashboard-overview.component.ts:845-904`): every join `joinType: 'left'` lowercase, every
     * join a NON-EMPTY `fields` list, and the row timestamp on `n.createdDate` because
     * `ns.NsNotificationChannel` extends `EntityBase` and has no audit columns of its own.
     *
     * The anchor is matched on `ot.alias`, never on the numeric object-type id.
     *
     * What it CANNOT reach, and this is a signed descope: notifications sent to the CASEWORKER.
     * `SmSigningInvitationService.recordRefusedDispatch:419-459` addresses those to
     * `caseworker.recipient()`, so their anchor is a `um.User`. They appear in this Verlauf as audit
     * events (`dispatchRefused`, `dispatchRetryFailed`, `originNotCheckable`, `notificationSent`)
     * instead - which is exactly why the surface must not claim to show everything.
     *
     * <h3>Why the channel name needs two hops</h3>
     * `NsNotificationChannel.channelType` is an `ns.NsNotificationTypeChannelType` - the CONFIGURED
     * pairing of a notification type with a channel, carrying the template, the subject text and the
     * per-channel print options. The human word for the channel („E-Mail“, „SMS“, „Brief“) lives one
     * level further down, on the `co.CoChannelType` it points at. Hence `ctt` is a pure hop with only
     * `{name: 'id'}` projected and `cct.name` is the value the row shows. Dropping the `ctt` list to
     * save the hop is exactly the mistake trap 3 describes: the whole of
     * `NsNotificationTypeChannelType` would then be projected, which on the sibling widget put
     * „Mail-Kanal: Farbdruck“, „Whatsapp-Vorlage“ and „Statischer Betreff“ into a curated table.
     *
     * <h3>The row's title is the notification TYPE, not its status</h3>
     * `nt.name` answers „what was sent“ („Einladung zur Unterschrift“), which is what a caseworker
     * scans a history for. The delivery OUTCOME travels separately, in `r.recipientStatus`, whose
     * German label and colour come from the value list on this pipe's form - the enum has thirteen
     * members (`SENT`, `DELIVERED`, `READ`, `FETCHED`, `ERROR`, `MISSING_CONTACT`,
     * `MISSING_APPROVAL`, `PENDING`, `PENDING_AFTER_RESTART`, `PROVIDED`, `DELETED`, `TEST`), far
     * more than a status word could be guessed for, and not one of them is spelled out in this file.
     *
     * <h3>An empty signer list is a valid input, not a skip</h3>
     * When Q1 returns nothing, `${query:signers:s.id}` resolves to an empty IN list and this query
     * returns zero rows. That is not an error and must not be special-cased upstream: keeping the
     * query in the batch is what keeps the loading, empty and error states of the Verlauf on ONE
     * code path instead of three.
     */
    private static signerNotificationsQuery(): QlQueryDto {
        return {
            name: SM_TL_Q_NOTIFICATIONS,
            type: 'view',
            dependsOn: [SM_TL_Q_SIGNERS],
            start: {
                type: 'entity',
                name: 'ns.NsNotificationChannelRecipient',
                as: 'r',
                fields: [{name: 'id'}, {name: 'objectId'}, {name: 'recipientStatus'},
                    {name: 'channelRecipientAddress'}, {name: 'errorMessage'}],
                joins: [
                    {type: 'entity', joinType: 'left', name: 'objectType', as: 'ot',
                        fields: [{name: 'id'}, {name: 'alias'}]},
                    {type: 'entity', joinType: 'left', name: 'notificationChannel', as: 'nc',
                        fields: [{name: 'id'}, {name: 'channelStatus'}],
                        joins: [
                            {type: 'entity', joinType: 'left', name: 'notification', as: 'n',
                                fields: [{name: 'id'}, {name: 'createdDate'}],
                                joins: [
                                    {type: 'entity', joinType: 'left', name: 'notificationType', as: 'nt',
                                        fields: [{name: 'id'}, {name: 'name'}]}
                                ]},
                            {type: 'entity', joinType: 'left', name: 'channelType', as: 'ctt',
                                fields: [{name: 'id'}],
                                joins: [
                                    {type: 'entity', joinType: 'left', name: 'channelType', as: 'cct',
                                        fields: [{name: 'id'}, {name: 'name'}]}
                                ]}
                        ]},
                    {type: 'entity', joinType: 'left', name: 'notificationContent', as: 'nco',
                        fields: [{name: 'id'}, {name: 'subject'}],
                        joins: [
                            {type: 'entity', joinType: 'left', name: 'document', as: 'doc',
                                fields: [{name: 'id'}, {name: 'name'}]}
                        ]}
                ]
            },
            fixedFilters: [
                FilterCriteria.create('ot.alias', FilterCriteria.cOperatorEqual, SM_TL_SIGNER_OBJECT_TYPE_ALIAS),
                FilterCriteria.create('r.objectId', FilterCriteria.cOperatorIn, '${query:signers:s.id}')
            ],
            sortings: [new Sorting('n.createdDate', false)]
        };
    }

    /**
     * Q3 - the audit backbone.
     *
     * Sorted on `sequenceNumber`, not on `eventTimestamp`: the sequence number is the order the hash
     * chain was written in, and two events written inside the same millisecond would otherwise swap
     * places and make the chain read as if it had been re-ordered. The merged list is sorted by time
     * afterwards, but within one timestamp this query's own order survives, which is what keeps a
     * `dispatchRefused` in front of the `notificationSent` that reports it.
     *
     * `signerId` is projected explicitly (the established in-tree way of asking for a foreign key in
     * `fields` - `mvs-pc-call-dial.component.ts:185`, `cw-case.service.ts:620`). A process-level
     * event has none, and that {@code null} is a statement, not a gap: it means „this concerns the
     * process, not one person“, and the lane rendering depends on it.
     */
    private static auditEventsQuery(signingProcessId: number): QlQueryDto {
        return {
            name: SM_TL_Q_AUDIT_EVENTS,
            type: 'view',
            start: {
                type: 'entity',
                name: 'sm.SmAuditEvent',
                as: 'a',
                fields: [{name: 'id'}, {name: 'signerId'}, {name: 'eventType'}, {name: 'actor'},
                    {name: 'details'}, {name: 'ipAddress'}, {name: 'eventTimestamp'},
                    {name: 'sequenceNumber'}]
            },
            fixedFilters: [
                FilterCriteria.create('a.signingProcessId', FilterCriteria.cOperatorEqual, signingProcessId)
            ],
            sortings: [new Sorting('a.sequenceNumber', true)]
        };
    }

    /**
     * Q4 - the placed signatures, read through `sm.SmSigningProcessSignerField`.
     *
     * <b>Via the field entity on purpose.</b> `sm.Signature` has NO foreign key to the process and
     * none to the signer; the only link between a signature and the process it belongs to is
     * `SmSigningProcessSignerField.signature_id`. A query starting at `sm.Signature` would have
     * nothing to filter on. Going through the field entity has a second, better consequence: the row
     * says WHICH field was signed (`fieldName`), which a signature-first read could not.
     *
     * <b>What it cannot deliver: the evidence level.</b> Verified against the model - no
     * `evidenceLevel` column exists on `sm.Signature` or on `sm.SmSigningProcessSigner`. The level is
     * DERIVED at runtime by `SmSignatureEvidenceService#deriveEvidenceLevel` out of the
     * authentication method, the address origin and the in-person identification record, and the only
     * persisted `SmEvidenceLevelEnum` column in the module is
     * `SmSignatureProfile.inPersonEvidenceLevel`, which is configuration rather than a fact about
     * this signature. So this query projects `authenticationMethodUsed` - the persisted input the
     * derivation reads - and the row must be labelled as the authentication method, never as the
     * evidence level. Presenting the input as the outcome would state a legal quality the data does
     * not carry.
     *
     * <h3>The signature join is LEFT, and that is load bearing</h3>
     * `SmSigningProcessSignerField.signature` is an optional `@ManyToOne`. A field row without one is
     * a real and frequent state - it is a PLACEMENT, a spot on the document that was derived or
     * placed by hand and is still waiting for a stroke. An inner join would drop exactly those rows,
     * and with them the only evidence that a signature was expected somewhere and never arrived,
     * which is the thing a dispute asks about first. The mapper distinguishes the two on
     * `sig.signed`, never on the presence of the join row.
     *
     * <h3>What is deliberately not projected</h3>
     * The four rectangle coordinates (`rectLlx`, `rectLly`, `rectUrx`, `rectUry`) locate the stamp on
     * the page to sub-point precision. They are geometry for the renderer and mean nothing in a
     * chronological history, so only `page` is taken - enough for „Seite 3“ in the detail line, and
     * nothing that invites a reader to interpret a bounding box. `documentHashAtSigning` and
     * `signatureImageHash` are likewise left out: they belong to the audit trail document, which is
     * produced for exactly that purpose, and a 64-character hex string in a timeline row is noise.
     */
    private static signatureFieldsQuery(): QlQueryDto {
        return {
            name: SM_TL_Q_SIGNATURE_FIELDS,
            type: 'view',
            dependsOn: [SM_TL_Q_SIGNERS],
            start: {
                type: 'entity',
                name: 'sm.SmSigningProcessSignerField',
                as: 'sf',
                fields: [{name: 'id'}, {name: 'signerId'}, {name: 'fieldName'}, {name: 'signedAt'},
                    {name: 'page'}],
                joins: [
                    {type: 'entity', joinType: 'left', name: 'signature', as: 'sig',
                        fields: [{name: 'id'}, {name: 'signatureTimeStamp'}, {name: 'signed'},
                            {name: 'ipAddress'}, {name: 'userAgent'}, {name: 'signatureMethod'},
                            {name: 'authenticationMethodUsed'}, {name: 'dmDocumentId'}]}
                ]
            },
            fixedFilters: [
                FilterCriteria.create('sf.signerId', FilterCriteria.cOperatorIn, '${query:signers:s.id}')
            ],
            sortings: [new Sorting('sf.signedAt', true)]
        };
    }

    /**
     * Q5 - the document versions of the process.
     *
     * Process-scoped, not signer-scoped, so it needs no dependency: `signingProcessId` is a real
     * foreign key here. Its rows therefore carry {@code signerId: null} - see Q3 on why that null is
     * a statement.
     *
     * <h3>What a version row means</h3>
     * `SmDocumentVersionTypeEnum` has five members and each is a different document in the life of
     * one process: `original` (what went in), `intermediate` (a partially signed state between two
     * signers), `signed` (the finished end document), `auditTrail` (the protocol of what happened,
     * produced at completion) and `customerCopy` (the copy handed to the signer). The Verlauf shows
     * the German word for whichever it is, taken from this pipe's own value list - the type carries
     * `@UiEnumValueInfo` on the backend, so no member is named in this file.
     *
     * <h3>Why `versionNumber` and not `createdDate` sorts this query</h3>
     * `versionNumber` is the intended succession of the document; `createdDate` is when a row
     * happened to be written. Two versions produced inside the same second - a signed document and
     * the audit trail that describes it, which are written together at completion - would swap under
     * a timestamp sort and read as if the protocol had been produced before the document it
     * protocols. The merged list is re-sorted by time afterwards, but a stable sort preserves this
     * order inside one timestamp, which is exactly the case that matters.
     *
     * <h3>The SHA-256 is shown, the document id is not</h3>
     * `sha256Hash` goes into the detail line because it is the one thing that makes a version row
     * checkable by a reader against a file they hold. `documentId` is projected but never displayed:
     * it exists so the row can OPEN the document through `DmDocumentService`, and a raw
     * `dm.DmDocument` primary key on screen tells a caseworker nothing.
     */
    private static documentVersionsQuery(signingProcessId: number): QlQueryDto {
        return {
            name: SM_TL_Q_DOCUMENT_VERSIONS,
            type: 'view',
            start: {
                type: 'entity',
                name: 'sm.SmDocumentVersion',
                as: 'dv',
                fields: [{name: 'id'}, {name: 'documentId'}, {name: 'versionType'},
                    {name: 'versionNumber'}, {name: 'description'}, {name: 'sha256Hash'},
                    {name: 'createdDate'}]
            },
            fixedFilters: [
                FilterCriteria.create('dv.signingProcessId', FilterCriteria.cOperatorEqual, signingProcessId)
            ],
            sortings: [new Sorting('dv.versionNumber', true)]
        };
    }

    /**
     * Q6 - the signing links issued for those signers.
     *
     * <b>`guid` is NOT projected, and must never be.</b> For the one-factor variants of the external
     * signing route that value is the ENTIRE access control - whoever holds the link signs. It is
     * stored in the clear and is only kept off the QL path by its own
     * `@UiFieldInfo(uiHidden = true)`, which `QlDataMeta#getSpecialAttributes` honours (pinned by
     * `SmSecretFieldQlExposureTest`). Asking for it here would be asking the platform to hand a live
     * credential to a screen; the Verlauf needs to say that a link was ISSUED, never what it was.
     *
     * Same alias-not-id rule for the anchor as Q2.
     *
     * <h3>What a row here does and does not say</h3>
     * `cc.ObjectActionGuid` is a REGISTRY of issued action credentials, not a log of what was done
     * with them. A row proves that a signing link was minted for a signer at `createdDate` under a
     * given `actionKey`; it says nothing about whether the link was delivered, opened, used or
     * revoked. All four of those are audit events (`linkDelivered`, `linkOpened`, `linkRevoked`,
     * `sessionStarted`) and reach the Verlauf through Q3. In particular the entity has NO revocation
     * column - a revoked link is a row that still exists next to a `linkRevoked` audit entry and a
     * `linkRevokedAt` on the signer - so this query must never be read as „the links that are still
     * valid“.
     *
     * <h3>Why it is a separate read and not a join</h3>
     * The link is anchored polymorphically, exactly like a notification: `objectId` is a plain
     * `Long`, there is no foreign key from the guid to `sm.SmSigningProcessSigner`, and no join can
     * be compiled from the signer to it. The dependency on Q1 plus an IN list over its ids is the
     * only route, which is why this query carries `dependsOn` rather than hanging off the signer
     * query as a reverse join.
     *
     * <h3>`actionKey`</h3>
     * The action the credential authorises, as a short technical key. It is projected because two
     * links minted for the same signer within a second are otherwise indistinguishable in the
     * history, and it is rendered as-is: inventing German prose for a key whose value set belongs to
     * `cc` and is not enumerated anywhere in `sm` would be a guess printed as a fact.
     */
    private static signingLinksQuery(): QlQueryDto {
        return {
            name: SM_TL_Q_SIGNING_LINKS,
            type: 'view',
            dependsOn: [SM_TL_Q_SIGNERS],
            start: {
                type: 'entity',
                name: 'cc.ObjectActionGuid',
                as: 'g',
                fields: [{name: 'id'}, {name: 'objectId'}, {name: 'actionKey'}, {name: 'createdDate'}],
                joins: [
                    {type: 'entity', joinType: 'left', name: 'objectType', as: 'got',
                        fields: [{name: 'id'}, {name: 'alias'}]}
                ]
            },
            fixedFilters: [
                FilterCriteria.create('got.alias', FilterCriteria.cOperatorEqual, SM_TL_SIGNER_OBJECT_TYPE_ALIAS),
                FilterCriteria.create('g.objectId', FilterCriteria.cOperatorIn, '${query:signers:s.id}')
            ],
            sortings: [new Sorting('g.createdDate', false)]
        };
    }

    // =================================================================================================
    // The fold
    // =================================================================================================

    /**
     * Folds the six pipes into one chronological list, newest first.
     *
     * ONE sort, over the concatenation of all five entry-producing mappers. Sorting each block and
     * concatenating the blocks would look identical for a process whose sources happen not to
     * interleave and would be wrong for every other one - the defect this design was explicitly told
     * not to reproduce.
     *
     * Newest first, matching the „Benachrichtigungen“ tab and the history convention of this repo. A
     * consumer wanting the evidentiary reading (oldest first, the order the hash chain was written
     * in) reverses the array; it must not re-sort it, because {@link compareByTimestampDesc} keeps
     * the audit query's own `sequenceNumber` order inside one timestamp and a second sort would lose
     * that.
     */
    private static mergeTimeline(result: QlQueryResult): SmSigningProcessTimelineDto {

        const signerForm = result.getForm(SM_TL_Q_SIGNERS);

        const lanes: SmTimelineLaneDto[] = (result.get(SM_TL_Q_SIGNERS) ?? []).map(row => (<SmTimelineLaneDto>{
            signerId: row['s.id'],
            signerName: row['s.signerName'] ?? '',
            signerRole: row['s.signerRole'] ?? null,
            signingOrder: row['s.signingOrder'],
            signerStatus: row['s.signerStatus'] ?? null,
            signerStatusValueListEntry: SmSigningProcessTimelineService.valueListEntry(
                signerForm, 's.signerStatus', row['s.signerStatus'])
        }));

        const entries: SmTimelineEntryDto[] = [
            ...SmSigningProcessTimelineService.mapAuditEvents(result),
            ...SmSigningProcessTimelineService.mapNotifications(result),
            ...SmSigningProcessTimelineService.mapSignatures(result),
            ...SmSigningProcessTimelineService.mapDocumentVersions(result),
            ...SmSigningProcessTimelineService.mapSigningLinks(result)
        ];

        entries.sort(SmSigningProcessTimelineService.compareByTimestampDesc);

        return {lanes: lanes, entries: entries};
    }

    /** Q3 -> rows. Presentation comes from the enum metadata, never from a table in this file. */
    private static mapAuditEvents(result: QlQueryResult): SmTimelineEntryDto[] {

        const form = result.getForm(SM_TL_Q_AUDIT_EVENTS);
        const entries: SmTimelineEntryDto[] = [];

        for (const row of (result.get(SM_TL_Q_AUDIT_EVENTS) ?? [])) {

            const timestamp = row['a.eventTimestamp'];
            if (!timestamp) {
                continue;
            }

            const meta = SmSigningProcessTimelineService.valueListEntry(form, 'a.eventType', row['a.eventType']);

            entries.push({
                timestamp: timestamp,
                source: SmTimelineSourceEnum.audit,
                signerId: row['a.signerId'] ?? null,
                title: SmSigningProcessTimelineService.eventTitle(meta, row['a.eventType']),
                detail: SmSigningProcessTimelineService.joinDetail([
                    row['a.details'],
                    row['a.actor'] ? `Akteur: ${row['a.actor']}` : null,
                    row['a.ipAddress'] ? `IP: ${row['a.ipAddress']}` : null
                ]),
                // No severity: the presentation of an audit row is the backend's own, and a second
                // severity derived from the event type here would be exactly the duplicate map the
                // design forbids.
                valueListEntry: meta,
                targetObjectAlias: 'sm.SmAuditEvent',
                targetObjectId: row['a.id'],
                documentId: null
            });
        }

        return entries;
    }

    /** Q2 -> rows. */
    private static mapNotifications(result: QlQueryResult): SmTimelineEntryDto[] {

        const form = result.getForm(SM_TL_Q_NOTIFICATIONS);
        const entries: SmTimelineEntryDto[] = [];

        for (const row of (result.get(SM_TL_Q_NOTIFICATIONS) ?? [])) {

            const timestamp = row['n.createdDate'];
            if (!timestamp) {
                continue;
            }

            const errorMessage: string = row['r.errorMessage'] ?? null;

            entries.push({
                timestamp: timestamp,
                source: SmTimelineSourceEnum.notification,
                signerId: row['r.objectId'] ?? null,
                title: row['nt.name'] ?? 'Benachrichtigung',
                detail: SmSigningProcessTimelineService.joinDetail([
                    row['cct.name'] ? `Kanal: ${row['cct.name']}` : null,
                    row['r.channelRecipientAddress'] ? `An: ${row['r.channelRecipientAddress']}` : null,
                    errorMessage
                ]),
                // Derived from a fact the ROW carries and that has no presentation metadata of its
                // own - a delivery that recorded an error. Not derived from any enum.
                severity: errorMessage ? 'danger' : undefined,
                valueListEntry: SmSigningProcessTimelineService.valueListEntry(
                    form, 'r.recipientStatus', row['r.recipientStatus']),
                targetObjectAlias: 'ns.NsNotificationChannelRecipient',
                targetObjectId: row['r.id'],
                documentId: row['doc.id'] ?? null
            });
        }

        return entries;
    }

    /**
     * Q4 -> rows.
     *
     * The timestamp falls back from `sf.signedAt` to `sig.signatureTimeStamp`: both record the same
     * moment and either may be the one that was written, but a row with neither is dropped rather
     * than admitted with a null sort key.
     *
     * The authentication method is labelled as the authentication method. It is NOT the evidence
     * level - see {@link signatureFieldsQuery} on why no evidence level exists to show.
     */
    private static mapSignatures(result: QlQueryResult): SmTimelineEntryDto[] {

        const form = result.getForm(SM_TL_Q_SIGNATURE_FIELDS);
        const entries: SmTimelineEntryDto[] = [];

        for (const row of (result.get(SM_TL_Q_SIGNATURE_FIELDS) ?? [])) {

            const timestamp = row['sf.signedAt'] ?? row['sig.signatureTimeStamp'];
            if (!timestamp) {
                continue;
            }

            const fieldName: string = row['sf.fieldName'] ?? null;
            const authMeta = SmSigningProcessTimelineService.valueListEntry(
                form, 'sig.authenticationMethodUsed', row['sig.authenticationMethodUsed']);

            entries.push({
                timestamp: timestamp,
                source: SmTimelineSourceEnum.signature,
                signerId: row['sf.signerId'] ?? null,
                title: fieldName ? `Unterschrift: ${fieldName}` : 'Unterschrift geleistet',
                detail: SmSigningProcessTimelineService.joinDetail([
                    authMeta?.label ? `Authentifizierung: ${authMeta.label}` : null,
                    row['sig.ipAddress'] ? `IP: ${row['sig.ipAddress']}` : null,
                    row['sig.userAgent'] ? `Gerät: ${row['sig.userAgent']}` : null,
                    row['sf.page'] != null ? `Seite ${row['sf.page']}` : null
                ]),
                // Derived from `sig.signed`, a plain Boolean with no presentation metadata of its
                // own: a field row that exists but is not signed is a placement, not a signature.
                severity: row['sig.signed'] === true ? 'success' : 'warn',
                valueListEntry: SmSigningProcessTimelineService.valueListEntry(
                    form, 'sig.signatureMethod', row['sig.signatureMethod']),
                targetObjectAlias: 'sm.SmSigningProcessSignerField',
                targetObjectId: row['sf.id'],
                documentId: row['sig.dmDocumentId'] ?? null
            });
        }

        return entries;
    }

    /** Q5 -> rows. Process-scoped, so every row carries {@code signerId: null}. */
    private static mapDocumentVersions(result: QlQueryResult): SmTimelineEntryDto[] {

        const form = result.getForm(SM_TL_Q_DOCUMENT_VERSIONS);
        const entries: SmTimelineEntryDto[] = [];

        for (const row of (result.get(SM_TL_Q_DOCUMENT_VERSIONS) ?? [])) {

            const timestamp = row['dv.createdDate'];
            if (!timestamp) {
                continue;
            }

            const meta = SmSigningProcessTimelineService.valueListEntry(
                form, 'dv.versionType', row['dv.versionType']);

            entries.push({
                timestamp: timestamp,
                source: SmTimelineSourceEnum.document,
                signerId: null,
                title: meta?.label ?? 'Dokumentversion',
                detail: SmSigningProcessTimelineService.joinDetail([
                    row['dv.versionNumber'] != null ? `Version ${row['dv.versionNumber']}` : null,
                    row['dv.description'],
                    row['dv.sha256Hash'] ? `SHA-256: ${row['dv.sha256Hash']}` : null
                ]),
                valueListEntry: meta,
                targetObjectAlias: 'sm.SmDocumentVersion',
                targetObjectId: row['dv.id'],
                documentId: row['dv.documentId'] ?? null
            });
        }

        return entries;
    }

    /** Q6 -> rows. The GUID itself never leaves the backend - see {@link signingLinksQuery}. */
    private static mapSigningLinks(result: QlQueryResult): SmTimelineEntryDto[] {

        const entries: SmTimelineEntryDto[] = [];

        for (const row of (result.get(SM_TL_Q_SIGNING_LINKS) ?? [])) {

            const timestamp = row['g.createdDate'];
            if (!timestamp) {
                continue;
            }

            entries.push({
                timestamp: timestamp,
                source: SmTimelineSourceEnum.link,
                signerId: row['g.objectId'] ?? null,
                title: 'Signaturlink ausgestellt',
                detail: SmSigningProcessTimelineService.joinDetail([
                    row['g.actionKey'] ? `Aktion: ${row['g.actionKey']}` : null
                ]),
                targetObjectAlias: 'cc.ObjectActionGuid',
                targetObjectId: row['g.id'],
                documentId: null
            });
        }

        return entries;
    }

    // =================================================================================================
    // Helpers
    // =================================================================================================

    /**
     * Reads ONE entry out of the value list the backend put on a QL pipe's form.
     *
     * This is the ONLY presentation source of this service. `form.formFields` is keyed by the
     * alias-prefixed result key of a `simple` pipe (`a.eventType`), which is precisely why the pipes
     * above must stay `simple`. The comparison is loose, matching the platform's own
     * {@code MvsFormValueListDto.retrieveValueFromList}: an enum ordinal may arrive as a number or as
     * a numeric string depending on the column's declared data type, and a strict comparison would
     * silently return no entry for the second case - a colourless row rather than an error.
     *
     * Returns {@code undefined} rather than a substitute when there is no list. A caller must render
     * that neutrally and must NOT fill the gap from a local table.
     */
    private static valueListEntry(form: unknown,
                                  fieldKey: string,
                                  value: unknown): MvsFormValueListEntryDto | undefined {

        if (value == null) {
            return undefined;
        }

        const entries: MvsFormValueListEntryDto[] = form?.['formFields']?.[fieldKey]?.['valueList']?.['entries'];

        if (!entries || entries.length === 0) {
            return undefined;
        }

        // eslint-disable-next-line eqeqeq
        return entries.find(entry => entry.key == value);
    }

    /**
     * Title of an audit row.
     *
     * The label is the backend's own {@code uiLabel}. The fallback NAMES the ordinal it could not
     * resolve instead of inventing a German word for it: an unresolvable ordinal means the frontend
     * met an enum member the tenant's metadata did not describe, and printing a guessed label there
     * would hide exactly the drift that has to be noticed. It is deliberately not a lookup table.
     */
    private static eventTitle(meta: MvsFormValueListEntryDto | undefined, eventType: unknown): string {
        return meta?.label ?? `Ereignis ${eventType}`;
    }

    /**
     * Joins the detail fragments of a row, dropping the absent ones.
     *
     * Returns {@code null} rather than an empty string when nothing is left, so a template can branch
     * on presence instead of on length.
     */
    private static joinDetail(parts: (string | null | undefined)[]): string | null {
        const present = parts.filter(part => part != null && String(part).trim().length > 0);
        return present.length > 0 ? present.join(' · ') : null;
    }

    /**
     * Newest first.
     *
     * Compares the parsed instants rather than the raw strings: the six sources are all `Instant`
     * columns and therefore all UTC, where a lexicographic compare would be correct too - but a
     * single column changed to a zoned type later would break a string compare silently and in only
     * some rows. When a value cannot be parsed the comparison falls back to the string order, which
     * keeps the sort total and stable instead of leaving the row at an arbitrary position.
     *
     * Equal timestamps compare as equal, so `Array.prototype.sort` (stable since ES2019) keeps the
     * insertion order - audit events first, in their own `sequenceNumber` order.
     */
    private static compareByTimestampDesc(left: SmTimelineEntryDto, right: SmTimelineEntryDto): number {

        const leftTime = Date.parse(left.timestamp);
        const rightTime = Date.parse(right.timestamp);

        if (Number.isNaN(leftTime) || Number.isNaN(rightTime)) {
            return right.timestamp.localeCompare(left.timestamp);
        }

        return rightTime - leftTime;
    }
}
